import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost, formatMoney, t, showAiConsentModal } from "../api_helper"
import { isOfflineError, showOfflinePlaceholder } from "../lib/offline_ui"

export default class extends Controller {
  static targets = ["messages", "input", "sendBtn", "voiceBtn", "anomalies", "inputArea", "premiumGate"]

  connect() {
    this.isRecording = false
    this.mediaRecorder = null
    this.load()
  }

  async load() {
    try {
      const data = await apiGet("/api/chat")
      this.renderHistory(data)
    } catch (e) {
      if (isOfflineError(e)) { showOfflinePlaceholder(this.messagesTarget, () => this.load()); return }
      if (e.status === 403 && e.message === "ai_consent_required") {
        const accepted = await showAiConsentModal()
        if (accepted) this.load()
        return
      }
      if (e.message?.includes("403")) {
        this.showPremiumGate()
      }
    }
  }

  renderHistory(data) {
    const msgs = data.messages || []
    if (msgs.length === 0) {
      this.addBubble("assistant", t("chat.welcome"))
    } else {
      msgs.forEach(m => this.addBubble(m.role, m.content))
    }

    if (data.anomalies?.length > 0) this.renderAnomalies(data.anomalies)

    this.scrollToBottom()
  }

  async sendMessage(e) {
    if (e?.type === "keydown" && e.key !== "Enter") return
    const text = this.inputTarget.value.trim()
    if (!text) return

    this.inputTarget.value = ""
    this.addBubble("user", text)
    this.showThinking()
    this.scrollToBottom()

    try {
      const data = await apiPost("/api/chat", { content: text })
      this.removeThinking()
      this.addBubble("assistant", data.assistant_message.content)
    } catch {
      this.removeThinking()
      this.addBubble("assistant", t("chat.error"))
    }
    this.scrollToBottom()
  }

  async toggleVoice() {
    if (this.isRecording) {
      this.stopRecording()
    } else {
      this.startRecording()
    }
  }

  async startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      this.mediaRecorder = new MediaRecorder(stream)
      this.audioChunks = []
      this.mediaRecorder.ondataavailable = (e) => this.audioChunks.push(e.data)
      this.mediaRecorder.onstop = () => this.processAudio()
      this.mediaRecorder.start()
      this.isRecording = true
      this.voiceBtnTarget.classList.replace("bg-slate-100", "bg-red-500")
      this.voiceBtnTarget.classList.replace("dark:bg-slate-800", "dark:bg-red-500")
      this.voiceBtnTarget.querySelector("svg").classList.replace("text-slate-400", "text-white")
    } catch { /* mic denied */ }
  }

  stopRecording() {
    if (this.mediaRecorder?.state === "recording") {
      this.mediaRecorder.stop()
      this.mediaRecorder.stream.getTracks().forEach(t => t.stop())
    }
    this.isRecording = false
    this.voiceBtnTarget.classList.replace("bg-red-500", "bg-slate-100")
    this.voiceBtnTarget.classList.replace("dark:bg-red-500", "dark:bg-slate-800")
    this.voiceBtnTarget.querySelector("svg").classList.replace("text-white", "text-slate-400")
  }

  async processAudio() {
    const blob = new Blob(this.audioChunks, { type: "audio/webm" })
    const formData = new FormData()
    formData.append("audio", blob, "voice.webm")

    this.addBubble("user", `[${t("chat.voice_btn")}]`)
    this.showThinking()
    this.scrollToBottom()

    try {
      const meta = document.querySelector('meta[name="csrf-token"]')
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "X-CSRF-Token": meta?.content || "", "Accept": "application/json" },
        credentials: "same-origin",
        body: formData
      })
      const data = await response.json()
      this.removeThinking()

      if (data.user_message) {
        const lastUser = this.messagesTarget.querySelectorAll("[data-role='user']")
        const placeholder = lastUser[lastUser.length - 1]
        if (placeholder) placeholder.querySelector(".msg-text").textContent = data.user_message.content
      }
      if (data.assistant_message) {
        this.addBubble("assistant", data.assistant_message.content)
      } else if (data.error) {
        this.addBubble("assistant", data.error)
      }
    } catch {
      this.removeThinking()
      this.addBubble("assistant", t("chat.error"))
    }
    this.scrollToBottom()
  }

  addBubble(role, content) {
    const isSystem = role === "system"
    const isUser = role === "user"
    const escaped = this.escapeHtml(content)
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\n/g, "<br>")

    const div = document.createElement("div")
    div.setAttribute("data-role", role)

    if (isSystem) {
      div.className = "flex justify-center"
      div.innerHTML = `
        <div class="max-w-[92%] px-4 py-3 rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/30 dark:to-violet-950/30 border border-indigo-200/50 dark:border-indigo-800/30">
          <div class="msg-text text-sm leading-relaxed text-slate-700 dark:text-slate-300">${escaped}</div>
        </div>`
    } else {
      div.className = `flex ${isUser ? "justify-end" : "justify-start"}`
      const bubbleClass = isUser
        ? "bg-indigo-500 text-white rounded-2xl rounded-br-sm"
        : "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-2xl rounded-bl-sm"
      div.innerHTML = `
        <div class="max-w-[85%] px-3.5 py-2.5 ${bubbleClass}">
          <div class="msg-text text-sm leading-relaxed">${escaped}</div>
        </div>`
    }
    this.messagesTarget.appendChild(div)
  }

  showThinking() {
    const div = document.createElement("div")
    div.id = "thinking"
    div.className = "flex justify-start"
    div.innerHTML = `
      <div class="px-3.5 py-2.5 bg-slate-100 dark:bg-slate-800 rounded-2xl rounded-bl-sm">
        <div class="text-sm text-slate-400">${t("chat.thinking")}</div>
      </div>`
    this.messagesTarget.appendChild(div)
  }

  removeThinking() { document.getElementById("thinking")?.remove() }

  renderAnomalies(anomalies) {
    if (!this.hasAnomaliesTarget) return
    const items = anomalies.slice(0, 2).map(a => {
      if (a.type === "category_spike") return `<span class="text-xs">${a.category_name}: +${a.change_pct}%</span>`
      return `<span class="text-xs">${a.category_name}: ${formatMoney(a.amount, a.currency)}</span>`
    }).join(" · ")

    this.anomaliesTarget.innerHTML = `
      <div class="bg-red-50 dark:bg-red-900/10 rounded-xl px-3 py-2">
        <div class="text-xs font-medium text-red-500 mb-0.5">${t("chat.anomalies_title")}</div>
        ${items}
      </div>`
    this.anomaliesTarget.classList.remove("hidden")
  }

  showPremiumGate() {
    if (!this.hasPremiumGateTarget) return
    this.messagesTarget.classList.add("hidden")
    if (this.hasInputAreaTarget) this.inputAreaTarget.classList.add("hidden")
    this.premiumGateTarget.classList.remove("hidden")
    this.premiumGateTarget.innerHTML = `
      <div class="pt-16 text-center px-4">
        <svg class="w-10 h-10 text-slate-300 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/></svg>
        <div class="text-sm font-medium mb-1">${t("chat.premium_required")}</div>
        <div class="text-xs text-slate-400 mb-4">${t("chat.premium_hint")}</div>
        <a href="/pwa/settings" class="inline-block px-6 py-2.5 rounded-xl text-sm font-semibold bg-indigo-500 text-white active:scale-95 transition-transform">
          ${t("premium.upgrade") || "Upgrade"}
        </a>
      </div>`
  }

  scrollToBottom() {
    requestAnimationFrame(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    })
  }

  escapeHtml(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
